/* eslint-disable camelcase */
/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { JsonObject, t } from '@superset-ui/core';
import { isString } from 'lodash';

export const hasTimeOffset = (
  series: JsonObject,
  timeCompare: string[],
): boolean =>
  isString(series.name)
    ? !!timeCompare.find(
        timeOffset =>
          // offset is represented as <offset>, group by list
          series.name.includes(`${timeOffset},`) ||
          // offset is represented as <metric>__<offset>
          series.name.includes(`__${timeOffset}`),
      )
    : false;

export const getOriginalSeries = (
  seriesName: string,
  timeCompare: string[],
): string => {
  let result = seriesName;
  timeCompare.forEach(compare => {
    // offset is represented as <offset>, group by list
    result = result.replace(`${compare},`, '');
    // offset is represented as <metric>__<offset>
    result = result.replace(`__${compare}`, '');
  });
  return result.trim();
};

/**
 * 翻译时间偏移字符串
 * 将英文的时间偏移描述翻译为中文
 * @param offset - 时间偏移字符串，如 "1 day ago", "1 year ago"
 * @returns 翻译后的字符串，如 "1天前", "1年前"
 */
export const translateTimeOffset = (offset: string): string => {
  // 预定义的翻译映射
  const offsetTranslations: Record<string, string> = {
    '1 day ago': t('1天前'),
    '1 week ago': t('1周前'),
    '28 days ago': t('28天前'),
    '30 days ago': t('30天前'),
    '1 month ago': t('1月前'),
    '52 weeks ago': t('52周前'),
    '1 year ago': t('1年前'),
    '104 weeks ago': t('104周前'),
    '2 years ago': t('2年前'),
    '156 weeks ago': t('156周前'),
    '3 years ago': t('3年前'),
  };

  // 检查是否在预定义映射中
  if (offsetTranslations[offset]) {
    return offsetTranslations[offset];
  }

  // 处理动态生成的偏移值，如 "X days ago", "X days after"
  // 格式: <number> <unit> <ago/after>
  const dynamicMatch = offset.match(
    /^(\d+)\s+(day|days|week|weeks|month|months|year|years)\s+(ago|after)$/,
  );
  if (dynamicMatch) {
    const [, num, unit, direction] = dynamicMatch;

    // 单位翻译映射
    const unitTranslations: Record<string, string> = {
      day: '天',
      days: '天',
      week: '周',
      weeks: '周',
      month: '月',
      months: '月',
      year: '年',
      years: '年',
    };

    // 方向翻译映射
    const directionTranslations: Record<string, string> = {
      ago: '前',
      after: '后',
    };

    const translatedUnit = unitTranslations[unit] || unit;
    const translatedDirection = directionTranslations[direction] || direction;

    return `${num}${translatedUnit}${translatedDirection}`;
  }

  // 如果无法匹配，返回原始值
  return offset;
};

/**
 * 翻译系列名称中的时间偏移后缀和计算类型前缀
 * 将带时间偏移后缀的系列名翻译为本地化显示名称
 * @param seriesName - 系列名称，如 "人工已接待会话量__1 day ago" 或 "difference__SUM(人工已接待会话量)__SUM(人工已接待会话量)__1 day ago"
 * @param timeCompare - 时间偏移数组，如 ["1 day ago"]
 * @returns 翻译后的系列名称，如 "人工已接待会话量__1天前" 或 "差异__SUM(人工已接待会话量)__SUM(人工已接待会话量)__1天前"
 */
export const translateSeriesName = (
  seriesName: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _timeCompare: string[],
): string => {
  // 如果没有系列名，直接返回
  if (!seriesName || typeof seriesName !== 'string') {
    return seriesName || '';
  }

  let result = seriesName;

  try {
    // 1. 翻译计算类型前缀 (difference, percentage, ratio)
    // 格式: <prefix>__<metric>__<metric>__<offset>
    const comparisonPrefixTranslations: Record<string, string> = {
      difference: t('差异'),
      percentage: t('百分比变化'),
      ratio: t('比率'),
    };

    // 匹配开头的计算类型前缀（只会有一个）
    const matchedPrefix = Object.keys(comparisonPrefixTranslations).find(
      prefix => result.startsWith(`${prefix}__`),
    );
    if (matchedPrefix) {
      const translated = comparisonPrefixTranslations[matchedPrefix];
      result = `${translated}__${result.slice(matchedPrefix.length + 2)}`;
    }

    // 2. 翻译时间偏移后缀
    // 格式: __<number> <unit> <ago/after>
    // 例如: __1 day ago, __30 days ago, __1 year ago
    const offsetPattern =
      /__(\d+)\s+(day|days|week|weeks|month|months|year|years)\s+(ago|after)/g;

    result = result.replace(offsetPattern, (_match, num, unit, direction) => {
      const translatedUnit =
        unit === 'day' || unit === 'days'
          ? '天'
          : unit === 'week' || unit === 'weeks'
          ? '周'
          : unit === 'month' || unit === 'months'
          ? '月'
          : unit === 'year' || unit === 'years'
          ? '年'
          : unit;
      const translatedDirection =
        direction === 'ago' ? '前' : direction === 'after' ? '后' : direction;
      return `__${num}${translatedUnit}${translatedDirection}`;
    });

    return result;
  } catch {
    // 如果翻译过程中出错，返回原始名称
    return seriesName;
  }
};
